// src/components/MultiplayerStatusBar.tsx
// SIMPLE VERSION - Clean dropdown selector

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./MultiplayerStatusBar.css";

interface MultiplayerStatusProps {
  socket: any;
  currentUser: {
    id: string;
    username: string;
  };
}

export const MultiplayerStatus: React.FC<MultiplayerStatusProps> = ({
  socket,
  currentUser,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<"available" | "do_not_disturb">("available");
  const [gameRoom, setGameRoom] = useState<string | null>(null);
  const [hasInvite, setHasInvite] = useState(false);
  const navigate = useNavigate();

  // Listen for game state changes
  useEffect(() => {
    if (!socket) return;

    socket.on("room_created", (data: any) => {
      setGameRoom(data.roomId);
    });

    socket.on("opponent_joined", () => {
      setHasInvite(true);
    });

    socket.on("challenge_received", () => {
      setHasInvite(true);
    });

    socket.on("game_finished", () => {
      setGameRoom(null);
      setHasInvite(false);
    });

    return () => {
      socket.off("room_created");
      socket.off("opponent_joined");
      socket.off("challenge_received");
      socket.off("game_finished");
    };
  }, [socket]);

  const handleStatusChange = (newStatus: "available" | "do_not_disturb") => {
    setStatus(newStatus);
    if (socket) {
      socket.emit("set_multiplayer_preference", {
        acceptInvitations: newStatus === "available",
      });
    }
    setIsOpen(false);
  };

  const getStatusDisplay = () => {
    if (gameRoom) return { icon: "🟡", text: "In Lobby" };
    if (status === "do_not_disturb") return { icon: "🔴", text: "DND" };
    return { icon: "🟢", text: "Available" };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="multiplayer-status">
      <button
        className={`status-trigger ${isOpen ? "open" : ""} ${hasInvite ? "has-notification" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="status-icon">{statusDisplay.icon}</span>
        <span className="status-text">{statusDisplay.text}</span>
        <span className="dropdown-arrow">▼</span>
        {hasInvite && <span className="notification-dot"></span>}
      </button>

      {isOpen && (
        <>
          <div className="status-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="status-menu">
            <div className="status-menu-header">
              <h4>Multiplayer Status</h4>
            </div>

            {gameRoom && (
              <div className="status-menu-item game-active">
                <span className="item-icon">🎮</span>
                <div className="item-content">
                  <div className="item-title">Game Active</div>
                  <div className="item-desc">Room: {gameRoom}</div>
                </div>
                <button
                  className="item-action"
                  onClick={() => {
                    navigate("/play-online", { state: { roomId: gameRoom } });
                    setIsOpen(false);
                  }}
                >
                  Join
                </button>
              </div>
            )}

            {hasInvite && (
              <div className="status-menu-item invite-pending">
                <span className="item-icon">⚔️</span>
                <div className="item-content">
                  <div className="item-title">New Invitation</div>
                  <div className="item-desc">Someone challenged you!</div>
                </div>
              </div>
            )}

            <div className="status-menu-divider"></div>

            <button
              className={`status-menu-item ${status === "available" ? "active" : ""}`}
              onClick={() => handleStatusChange("available")}
            >
              <span className="item-icon">🟢</span>
              <div className="item-content">
                <div className="item-title">Available</div>
                <div className="item-desc">Accept game invites</div>
              </div>
            </button>

            <button
              className={`status-menu-item ${status === "do_not_disturb" ? "active" : ""}`}
              onClick={() => handleStatusChange("do_not_disturb")}
            >
              <span className="item-icon">🔴</span>
              <div className="item-content">
                <div className="item-title">Do Not Disturb</div>
                <div className="item-desc">Block all invites</div>
              </div>
            </button>

            <div className="status-menu-divider"></div>

            <button
              className="status-menu-item"
              onClick={() => {
                navigate("/multiplayer");
                setIsOpen(false);
              }}
            >
              <span className="item-icon">🌐</span>
              <div className="item-content">
                <div className="item-title">Go to Multiplayer</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MultiplayerStatus;
